// FILE: src/components/HeaderUserMenu.js
//
// Fixed-position user menu mounted at the top-right of the viewport. Renders
// as a sibling to <App />, not inside it, so App.js stays untouched in
// Phases A-D and the menu is always visible regardless of which view App
// renders. Phase E reparents these surfaces into a real view structure
// (see PLAN-phase-a-design.md §6.9.1).
//
// Anonymous:  [ Sign in ] button → opens LearnerAuth modal.
// Signed in:  avatar/initial → dropdown with:
//                • Your Journey            (Phase A — calm baseline overlay)
//                • Performance History     (Phase B — sessions + trend chart)
//                • Admin                   (verified admins only — allow-list gated)
//                • Sign out
//
// All learner surfaces open as portal overlays — App.js never knows.

import React, { useEffect, useRef, useState } from 'react';
import { useLearnerAuth } from '../../contexts/LearnerAuthContext';
import { updateLearnerProfile } from '../../services/learnerProfileService';
import { checkAdminStatus } from '../../services/adminStatusService';
import useMediaQuery from '../../hooks/useMediaQuery';
import LearnerAuth from './LearnerAuth';
import BaselineConfidence from '../readiness/BaselineConfidence';
import FoundingTesterWelcome from '../founding-testers/FoundingTesterWelcome';
import FoundingTesterBadge from '../founding-testers/FoundingTesterBadge';
import { resetMyFoundingTesterCache } from '../../services/foundingTesterService';
import styles from './HeaderUserMenu.module.css';
import { isFeatureEnabled } from '../../config/featureFlags';

export default function HeaderUserMenu() {
  const { user, profile, loading, signOut, refresh } = useLearnerAuth();
  const [authOpen, setAuthOpen]               = useState(false);
  const [authTab, setAuthTab]                 = useState('signin');
  const [menuOpen, setMenuOpen]               = useState(false);
  // Phase D — BaselineConfidence overlay. Triggered by the
  // 'pm-pixel:baseline-set' CustomEvent that sessionService dispatches
  // after a learner's first qualifying signed-in session per cert.
  const [baselineState, setBaselineState]     = useState({ open: false, examId: null, readiness: null });
  // Founding Tester welcome celebration — opened when the founding_tester
  // achievement is newly earned (granted at registration).
  const [foundingOpen, setFoundingOpen]       = useState(false);
  // Admin entry (menu-gated). True only when the signed-in email passes the
  // admin allow-list check — see adminStatusService. Non-admins never see
  // the Admin item; AdminGate re-verifies on open regardless.
  const [isAdmin, setIsAdmin]                 = useState(false);
  const dropdownRef                            = useRef(null);
  // Phone-class viewport: the fixed sign-in callout shrinks to a single
  // compact button so it doesn't crowd the hero avatar. Matches the
  // @media (max-width: 600px) breakpoint already used by HomePage.css.
  const isMobile                               = useMediaQuery('(max-width: 600px)');

  useEffect(() => {
    function onBaselineSet(e) {
      const detail = e?.detail || {};
      if (!detail.examId) return;
      setBaselineState({ open: true, examId: detail.examId, readiness: detail.readiness || null });
    }
    window.addEventListener('pm-pixel:baseline-set', onBaselineSet);
    return () => window.removeEventListener('pm-pixel:baseline-set', onBaselineSet);
  }, []);

  // Open the LearnerAuth modal on demand. Public surfaces (e.g. the Founding
  // Tester landing page) dispatch 'pm-pixel:auth' with an optional
  // { tab: 'signin' | 'signup' | 'reset' } detail so a CTA can drop the visitor
  // straight onto the Sign-up tab without threading props through App.js.
  useEffect(() => {
    function onAuth(e) {
      const tab = e?.detail?.tab;
      if (tab) setAuthTab(tab);
      setAuthOpen(true);
    }
    window.addEventListener('pm-pixel:auth', onAuth);
    return () => window.removeEventListener('pm-pixel:auth', onAuth);
  }, []);

  // Listen for the Founding Tester achievement (fired once at registration) and
  // pop the welcome celebration. Other achievement keys are ignored here.
  useEffect(() => {
    function onEarned(e) {
      // Enrollment closed: the award no longer fires for new registrations,
      // and the welcome celebration is retired with it (defense in depth).
      if (!isFeatureEnabled('foundingTesterEnrollmentOpen')) return;
      if (e?.detail?.key === 'founding_tester') setFoundingOpen(true);
    }
    window.addEventListener('pm-pixel:achievement-earned', onEarned);
    return () => window.removeEventListener('pm-pixel:achievement-earned', onEarned);
  }, []);

  // Resolve admin status whenever the signed-in email changes. Cached per
  // email inside the service, so this fires the RPC at most once per user
  // per page load.
  useEffect(() => {
    let cancelled = false;
    // Reset on any email change; only flip to true on a confirmed positive so
    // the common non-admin path never schedules a post-resolve state update.
    setIsAdmin(false);
    if (!user?.email) return undefined;
    Promise.resolve(checkAdminStatus(user.email)).then((ok) => {
      if (!cancelled && ok) setIsAdmin(true);
    });
    return () => { cancelled = true; };
  }, [user?.email]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  // Phase E2 — the synthesized PMP-practice hand-off used to live here for
  // overlay CTAs ("Start a PMP practice session"). With learner pages now
  // mounted in App.js's view tree, page-level CTAs navigate via
  // pm-pixel:navigate → view='home' and the learner picks from there.
  // A future polish step can deep-link directly into a cert+mode if the
  // active cert is known from the learner profile.

  if (loading) {
    return <div className={styles.placeholder} aria-hidden="true" />;
  }

  if (!user) {
    return (
      <>
        <div className={styles.root}>
          <button
            type="button"
            className={styles.signInBtn}
            onClick={() => setAuthOpen(true)}
            data-testid="header-sign-in"
          >
            {isMobile ? 'Sign In' : 'Sign In for Personalized Coaching'}
          </button>
          <p className={styles.signInTagline}>
            Save your history, track readiness and get targeted study guidance.
          </p>
        </div>
        <LearnerAuth
          isOpen={authOpen}
          initialTab={authTab}
          onClose={() => { setAuthOpen(false); setAuthTab('signin'); }}
        />
      </>
    );
  }

  const initial = (profile?.display_name || user.email || '?').trim().charAt(0).toUpperCase();
  const label   = profile?.display_name || user.email;

  // Pace coaching preferences (Phase 7). Default ON; targeted-test pacing OFF
  // unless opted in. Persisted on the learner profile; refresh() reloads it so
  // the active exam picks up the change.
  const paceOn = profile ? profile.pace_coaching_enabled !== false : true;
  const paceTargetedOn = profile ? profile.pace_coaching_in_targeted === true : false;
  async function togglePacePref(col, current) {
    if (!user) return;
    try {
      await updateLearnerProfile(user.id, { [col]: !current });
      if (typeof refresh === 'function') await refresh();
    } catch (_) {
      /* best-effort — preference will retry on next toggle */
    }
  }

  // Phase E2 — dispatch a window event so App.js's view-state listener
  // updates the active view. Keeps HeaderUserMenu out of App's props tree
  // for now; a follow-up moves the menu into App's render directly.
  function navigate(view) {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    try {
      window.dispatchEvent(new CustomEvent('pm-pixel:navigate', { detail: { view } }));
    } catch (_) { /* CustomEvent unavailable — non-fatal */ }
  }

  return (
    <>
      <div className={styles.root} ref={dropdownRef}>
        <button
          type="button"
          className={styles.avatarBtn}
          onClick={() => setMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          data-testid="header-user-menu-trigger"
        >
          <span className={styles.avatar} aria-hidden="true">{initial}</span>
          <span className={styles.label}>{label}</span>
          <FoundingTesterBadge auto size={20} showLabel={false} />
          <span className={styles.caret} aria-hidden="true">▾</span>
        </button>
        {menuOpen && (
          <ul className={styles.menu} role="menu" data-testid="header-user-menu">
            <li role="menuitem">
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => { setMenuOpen(false); navigate('dashboard'); }}
                data-testid="header-menu-dashboard"
              >
                Dashboard
              </button>
            </li>
            <li role="menuitem">
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => { setMenuOpen(false); navigate('history'); }}
                data-testid="header-menu-history"
              >
                Performance History
              </button>
            </li>
            <li role="menuitem">
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => { setMenuOpen(false); navigate('plan'); }}
                data-testid="header-menu-plan"
              >
                Study Plan
              </button>
            </li>
            <li role="menuitem">
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => { setMenuOpen(false); navigate('readiness'); }}
                data-testid="header-menu-readiness"
              >
                Readiness
              </button>
            </li>
            <li role="menuitem">
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => { setMenuOpen(false); navigate('milestones'); }}
                data-testid="header-menu-milestones"
              >
                Milestones
              </button>
            </li>
            <li role="menuitem">
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => { setMenuOpen(false); navigate('home'); }}
                data-testid="header-menu-home"
              >
                Take a session
              </button>
            </li>
            <li role="menuitem">
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => { setMenuOpen(false); navigate('founding-testers'); }}
                data-testid="header-menu-founding-testers"
              >
                Founding Testers
              </button>
            </li>
            <li role="menuitem" className={styles.menuDivider} aria-hidden="true" />
            <li role="menuitem">
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => togglePacePref('pace_coaching_enabled', paceOn)}
                data-testid="header-menu-pace-toggle"
                role="menuitemcheckbox"
                aria-checked={paceOn}
              >
                Pace coaching (Full & Practice) · {paceOn ? 'On' : 'Off'}
              </button>
            </li>
            {paceOn && (
              <li role="menuitem">
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => togglePacePref('pace_coaching_in_targeted', paceTargetedOn)}
                  data-testid="header-menu-pace-targeted"
                  role="menuitemcheckbox"
                  aria-checked={paceTargetedOn}
                >
                  Also in Targeted Tests · {paceTargetedOn ? 'On' : 'Off'}
                </button>
              </li>
            )}
            <li role="menuitem" className={styles.menuDivider} aria-hidden="true" />
            {isAdmin && (
              <li role="menuitem">
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => { setMenuOpen(false); navigate('admin'); }}
                  data-testid="header-menu-admin"
                >
                  ⚙️ Admin
                </button>
              </li>
            )}
            <li role="menuitem">
              <button
                type="button"
                className={styles.menuItem}
                onClick={async () => { setMenuOpen(false); resetMyFoundingTesterCache(); await signOut(); }}
                data-testid="header-menu-signout"
              >
                Sign out
              </button>
            </li>
          </ul>
        )}
      </div>

      {/* Phase E2 — LearnerJourney + PerformanceHistory used to mount here as
          portal overlays. They're now full pages in App.js's view tree
          (view='dashboard' / view='history'). HeaderUserMenu just dispatches
          pm-pixel:navigate events and the App routes the active view.
          BaselineConfidence stays here because it's a TRANSIENT modal — fires
          on first qualifying signed-in session and dismisses to the dashboard. */}
      <FoundingTesterWelcome
        isOpen={foundingOpen}
        onClose={() => setFoundingOpen(false)}
        displayName={profile?.display_name || null}
        onExplore={() => { setFoundingOpen(false); navigate('dashboard'); }}
      />

      <BaselineConfidence
        isOpen={baselineState.open}
        onClose={() => setBaselineState({ open: false, examId: null, readiness: null })}
        takenExamId={baselineState.examId}
        readiness={baselineState.readiness}
        displayName={profile?.display_name || null}
        onStartPractice={(_examId, _focusPhase) => {
          // Close the overlay and route the learner to a session.
          setBaselineState({ open: false, examId: null, readiness: null });
          navigate('home');
        }}
      />
    </>
  );
}
